import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { OnboardingConfigService } from '@app/onboarding/services/onboarding-config.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { FooterButtonStates } from '@app/onboarding/components/onboarding-footer/onboarding-footer.model';
import { TranslateService } from '@ngx-translate/core';
import { OnboardingStep } from '@app/onboarding/onboarding.enums';
import { JobRole } from '@app/onboarding/services/onboarding.service';
import { Observable } from 'rxjs';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TrackerStep } from '@app/onboarding/components/tracker/tracker.component';
import { AuthService } from '@app/shared/services/auth.service';

@Component({
  selector: 'dgx-role-select',
  templateUrl: './role-select.component.html',
  styleUrls: ['./role-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleSelectComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public templatePrefix: string;
  public featuredLoading: boolean = true;
  public featuredRoles: Observable<JobRole[]> =
    this.onboardingStateService.featuredRoleTargets$;
  public jobRoleIntegrationProvider: string = '';
  public i18n = this.translateService.instant([
    'OnboardingFooter_PreviousStep',
    'OnboardingFooter_NextStep',
    'OnboardingFooter_SkipStep',
    'Onboarding_Step2Title',
    'Onboarding_Step2TitleIntegration',
    'Onboarding_Step2InstructionsIntegration',
    'Onboarding_Step2Instructions',
    'Onboarding_RolesStepTitle',
  ]);
  private footerButtonStates: FooterButtonStates;

  public get onboardingRoleTitle(): string {
    return this.i18n.Onboarding_RolesStepTitle;
  }

  public get onboardingRoleDescription(): string {
    return this.onboarding_RolesStepDescription;
  }

  public get onboarding_RolesStepDescription(): string {
    return this.translateService.instant(`Onboarding_RolesStepDescription`, {
      orgName: this.onboardingConfigService.organizationNameInOnboarding,
    });
  }
  constructor(
    private onboardingConfigService: OnboardingConfigService,
    private onboardingStateService: OnboardingStateService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private trackerService: TrackerService,
    private authService: AuthService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.footerButtonStates = {
      currentStep: OnboardingStep.roleselect,
      disableNextStep: false,
      previousButton: this.i18n.OnboardingFooter_PreviousStep,
      nextButton: '',
      skipButton: this.i18n.OnboardingFooter_SkipStep,
    };
    this.onboardingConfigService.config.subscribe((config) => {
      this.templatePrefix = config.templatePrefix;
      this.initTrackerSteps(this.onboardingStateService.currentRole);
    });
    this.onboardingStateService.currentRole$
      .pipe(this.takeUntilDestroyed())
      .subscribe((role) => {
        this.onRoleSelected({ selected: !!role, role: role });
        this.cdr.detectChanges();
      });
    this.onboardingStateService.currentStepChange(OnboardingStep.roleselect);
  }

  public getIntegrationTitle(): string {
    return this.translateService.instant(
      'Onboarding_Step2InstructionsIntegration',
      { integrationName: this.jobRoleIntegrationProvider }
    );
  }

  /**
   * On user action selecting/deselection role
   * trigger button state change, and tracker state change
   *
   * @param roleSelected - true a role is selected
   */
  public onRoleSelected(obj: { selected: boolean; role: JobRole }) {
    const { selected, role } = obj;
    const disableResumeParsing: boolean =
      this.authService.authUser?.disableResumeParsingInOnboarding;
    const nextRoleUrl: string = !disableResumeParsing
      ? '/getstarted/resume'
      : '/getstarted/review';
    const steps = this.onboardingConfigService.getTrackerSteps(
      this.templatePrefix,
      OnboardingStep.roleselect
    );
    const consumerUser: boolean = this.authService.authUser.defaultOrgInfo
      ? false
      : true;
    // Update the tracker state on role change, but dont update the tracker until (next pressed)
    if (!selected || (selected && role?.skills.length <= 0) || consumerUser) {
      // consumer users should not see assigned skills page
      // remove the assigned skills step if the role does not have skills
      const i = steps.findIndex(
        (step) => step.stepName === OnboardingStep.assigned
      );
      if (i && i !== -1) {
        steps.splice(i, 1);
        // update the tracker to disable steps but don't remove the step until the user clicks next.
        const updateDisabledSteps = this.updateIsDisabledSteps(
          this.onboardingStateService.trackerSteps,
          OnboardingStep.roleselect,
          true
        );
        this.onboardingStateService.updateTrackerStep(updateDisabledSteps);
        this.cdr.detectChanges();
      }
    }
    // consumer users should never see assigned skills page
    this.onboardingConfigService.footerButtonStateChange({
      ...this.footerButtonStates,
      nextButton: selected ? this.i18n.OnboardingFooter_NextStep : '',
      skipButton: !selected ? this.i18n.OnboardingFooter_SkipStep : '',
      skipUrl: !selected ? nextRoleUrl : '',
      nextUrl:
        (selected && role?.skills?.length === 0) || consumerUser
          ? nextRoleUrl
          : '',
    });
    this.onboardingStateService.futureTrackerStateChange(steps);
    const trackingType = role?.featured ? 'Featured' : 'User Entered';
    this.trackerService.trackEventData({
      action: selected
        ? 'Onboarding Role Selected'
        : 'Onboarding Role Deselected',
      category: 'Onboarding',
      properties: {
        RoleType: trackingType,
        RoleId: role?.id,
        RoleName: role,
      },
    });
  }

  /**
   * Disable tracker steps that are not active
   * @param steps all tracker steps
   * @param activeStep step that is actice
   * @param disableOtherSteps should the other steps be diabled
   */
  private updateIsDisabledSteps(
    steps: TrackerStep[],
    activeStep: OnboardingStep,
    disableOtherSteps: boolean = false
  ): TrackerStep[] {
    steps.forEach((step) => {
      if (step.stepName !== activeStep) {
        step.isDisabled = disableOtherSteps;
      }
    });
    return steps;
  }

  /**
   * Initialize the tracker steps for the role select.
   * If coming into the role select page with a role selected and it has skills this page will show
   * 3 dots in the tracker, else it will only show 3.
   * Dots should not update on selection of the role only on Next/Skip step.
   *
   * @param currentRole Current role selected
   */
  private initTrackerSteps(currentRole: JobRole) {
    const consumerUser: boolean = this.authService.authUser.defaultOrgInfo
      ? false
      : true;

    if (!!currentRole && currentRole?.skills.length > 0 && !consumerUser) {
      this.onboardingStateService.updateTrackerStep(
        this.onboardingConfigService.getTrackerSteps(
          this.templatePrefix,
          OnboardingStep.roleselect,
          !!currentRole
        )
      );
    } else {
      const steps = this.onboardingConfigService.getTrackerSteps(
        this.templatePrefix,
        OnboardingStep.roleselect,
        !!currentRole
      );
      const i = steps.findIndex(
        (step) => step.stepName === OnboardingStep.assigned
      );
      // remove the assigned skills step if the role does not have skills
      if (i !== -1) {
        steps.splice(i, 1);
      }
      this.onboardingStateService.updateTrackerStep(steps);
    }
  }
}
